import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { MeasurementContext } from "@/hooks/useEstimates";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: MeasurementContext | null;
  onSubmit: (ctx: MeasurementContext) => void;
  title?: string;
}

const FIELDS: { key: keyof MeasurementContext; label: string; unit: string }[] = [
  { key: "roof_sf",       label: "Roof Area",    unit: "SF" },
  { key: "eaves_lf",      label: "Eaves",        unit: "LF" },
  { key: "rakes_lf",      label: "Rakes",        unit: "LF" },
  { key: "ridges_lf",     label: "Ridges",       unit: "LF" },
  { key: "hips_lf",       label: "Hips",         unit: "LF" },
  { key: "valleys_lf",    label: "Valleys",      unit: "LF" },
  { key: "headwalls_lf",  label: "Headwalls",    unit: "LF" },
  { key: "sidewalls_lf",  label: "Sidewalls",    unit: "LF" },
  { key: "chimneys_ea",   label: "Chimneys",     unit: "EA" },
  { key: "pipe_boots_ea", label: "Pipe Boots",   unit: "EA" },
];

export function RoofingMeasurementsDialog({ open, onOpenChange, initial, onSubmit, title }: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [waste, setWaste] = useState<string>("10");

  useEffect(() => {
    if (!open) return;
    const next: Record<string, string> = {};
    FIELDS.forEach(f => {
      const v = initial?.[f.key];
      next[f.key] = v != null ? String(v) : "";
    });
    setValues(next);
    setWaste(initial?.waste_pct != null ? String(initial.waste_pct) : "10");
  }, [open, initial]);

  const ctx = useMemo<MeasurementContext>(() => {
    const out: MeasurementContext = { waste_pct: parseFloat(waste) || 0 };
    FIELDS.forEach(f => {
      const n = parseFloat(values[f.key]);
      if (Number.isFinite(n)) (out as any)[f.key] = n;
    });
    return out;
  }, [values, waste]);

  const roofSF = ctx.roof_sf ?? 0;
  const baseSquares = roofSF / 100;
  const wasted = baseSquares * (1 + (ctx.waste_pct ?? 0) / 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title ?? "Roof Measurements"}</DialogTitle>
          <DialogDescription>
            Enter takeoff dimensions. Quantities on the estimate will be computed from these.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {FIELDS.map(f => (
            <div key={f.key}>
              <Label className="text-xs">{f.label} ({f.unit})</Label>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={values[f.key] ?? ""}
                onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                placeholder="0"
                className="h-9"
              />
            </div>
          ))}
          <div>
            <Label className="text-xs">Waste %</Label>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.1"
              value={waste}
              onChange={(e) => setWaste(e.target.value)}
              className="h-9"
            />
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-md border p-3 bg-muted/30">
            <div className="text-xs text-muted-foreground">Base Squares</div>
            <div className="font-mono text-base">{baseSquares.toFixed(2)} SQ</div>
          </div>
          <div className="rounded-md border p-3 bg-muted/30">
            <div className="text-xs text-muted-foreground">With {ctx.waste_pct ?? 0}% Waste</div>
            <div className="font-mono text-base">{wasted.toFixed(2)} SQ</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onSubmit(ctx); onOpenChange(false); }}>Apply Measurements</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
